/* =========================================================
   UFH KIT BUILDER — JS
   Lead capture + build via Shopify App Proxy -> Worker
========================================================= */

const HEAT_PROXY_PATH = "/apps/heat-calc";
const CRM_SOURCE_TAG = "ufh-kitbuilder-preview";
const INTERNAL_MODE = Boolean(window.UFH_CALC_CONFIG?.internalMode);

async function heatProxy(payload) {
  const res = await fetch(HEAT_PROXY_PATH, {
    method: "POST",
    headers: { "Content-Type": "application/json", "Accept": "application/json" },
    body: JSON.stringify(payload),
  });

  const text = await res.text();
  let data = null;
  try { data = JSON.parse(text); } catch {}

  if (!data) throw new Error(`Proxy returned non-JSON. First 200 chars: ${text.slice(0, 200)}`);
  if (!data.ok) throw new Error(data.error || "Proxy request failed");
  return data;
}

function escapeHtml(str) {
  return String(str || "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function formatMoney(amount, currency = "GBP") {
  const n = Number(amount || 0);
  const c = String(currency || "GBP").toUpperCase();
  const locale = c === "EUR" ? "en-IE" : "en-GB";
  const symbol = c === "EUR" ? "€" : "£";

  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: c,
    }).format(n);
  } catch {
    return `${symbol}${n.toFixed(2)}`;
  }
}

function calcTotalsFromLines(lines, vatRate = 0.2) {
  const currency = (lines.find(x => x.currency)?.currency) || "GBP";
  const exVat = (lines || []).reduce((sum, l) => {
    const qty = Number(l.qty || 0);
    const unit = Number(l.price || 0);
    return sum + (unit * qty);
  }, 0);
  const incVat = exVat * (1 + vatRate);
  return { currency, exVat, incVat };
}

function getInternalMarketSettings() {
  const quoteCountry = String(
    document.getElementById("ufhInternalQuoteCountry")?.value || "GB"
  ).toUpperCase();

  return {
    quoteCountry: quoteCountry === "IE" ? "IE" : "GB",
    quoteCurrency: quoteCountry === "IE" ? "EUR" : "GBP",
  };
}

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || "").trim());
}

function normaliseUkIePhone(phone, country = "UK") {
  let p = String(phone || "").trim();
  if (!p) return "";

  p = p.replace(/[^\d+]/g, "");

  if (p.startsWith("00")) p = "+" + p.slice(2);
  if (p.startsWith("+44") || p.startsWith("+353")) return p;
  if (p.startsWith("44")) return "+" + p;
  if (p.startsWith("353")) return "+" + p;

  if (p.startsWith("0")) {
    if (String(country).toUpperCase() === "IE") return "+353" + p.slice(1);
    return "+44" + p.slice(1);
  }

  return p;
}

/* =========================
   DOM + state
========================= */
const els = {};
let calcRunId = 0;
let lastKitLines = [];
let lastUFHCalc = null;
let leadCaptured = false;
let lastInternalPdfUrl = "";

document.addEventListener("DOMContentLoaded", () => {
  els.floors = document.getElementById("ufhFloors");
  els.bathrooms = document.getElementById("ufhBathrooms");
  els.area0 = document.getElementById("ufhArea0");
  els.area1Wrap = document.getElementById("ufhArea1Wrap");
  els.area1 = document.getElementById("ufhArea1");
  els.area2Wrap = document.getElementById("ufhArea2Wrap");
  els.area2 = document.getElementById("ufhArea2");
  els.area3Wrap = document.getElementById("ufhArea3Wrap");
  els.area3 = document.getElementById("ufhArea3");

  els.spacing = document.getElementById("ufhSpacing");
  els.pipeType = document.getElementById("ufhPipeType");
  els.fixing = document.getElementById("ufhFixing");

  els.zones = document.getElementById("ufhZones");
  els.controllerType = document.getElementById("ufhControllerType");
  els.controllerColour = document.getElementById("ufhControllerColour");

  els.projectStage = document.getElementById("ufhProjectStage");
  els.name = document.getElementById("ufhName");
  els.email = document.getElementById("ufhEmail");
  els.phone = document.getElementById("ufhPhone");
  els.phoneCountry = document.getElementById("ufhPhoneCountry");

  els.calcBtn = document.getElementById("ufhCalculateBtn");
  els.resetBtn = document.getElementById("ufhResetBtn");
  els.addBtn = document.getElementById("ufhAddToCartBtn");
  els.exportBtn = document.getElementById("ufhExportCsvBtn");
  els.printBtn = document.getElementById("ufhPrintQuoteBtn");

  els.msg = document.getElementById("ufhMsg");
  els.totalArea = document.getElementById("ufhTotalArea");
  els.pipeRequired = document.getElementById("ufhPipeRequired");
  els.circuits = document.getElementById("ufhCircuits");

  els.exVat = document.getElementById("ufhExVat");
  els.incVat = document.getElementById("ufhIncVat");
  els.kitBody = document.getElementById("ufhKitBody");

  if (els.floors) els.floors.addEventListener("change", onFloorsChange);
  if (els.floors) els.floors.addEventListener("input", onFloorsChange);
  if (els.calcBtn) els.calcBtn.addEventListener("click", calculateAndRender);
  if (els.resetBtn) els.resetBtn.addEventListener("click", resetAll);
  if (els.addBtn) els.addBtn.addEventListener("click", addKitToCart);
  if (els.exportBtn) els.exportBtn.addEventListener("click", exportKitToCsv);
  if (els.printBtn) els.printBtn.addEventListener("click", openInternalQuotePdf);

  els.phone?.addEventListener("input", () => {
    const raw = String(els.phone.value || "").replace(/\s+/g, "");

    if (raw.startsWith("+44") || raw.startsWith("0044") || raw.startsWith("44")) {
      els.phoneCountry.value = "UK";
    } else if (raw.startsWith("+353") || raw.startsWith("00353") || raw.startsWith("353")) {
      els.phoneCountry.value = "IE";
    }
  });

  onFloorsChange();
  resetAll();
});

function updatePrimaryButtonState() {
  if (!els.calcBtn) return;
  els.calcBtn.textContent = leadCaptured ? "Update quote" : "View Quote Now";
}

function getMaxFloors() {
  return INTERNAL_MODE ? 30 : 4;
}

function floorLabel(index) {
  if (index === 0) return "Ground floor";
  if (index === 1) return "First floor";
  if (index === 2) return "Second floor";
  if (index === 3) return "Third floor";
  return `Floor ${index + 1}`;
}

function onFloorsChange() {
  const holder = document.getElementById("ufhFloorAreas");
  if (!holder) return;

  const raw = String(els.floors?.value || "").trim();

  if (!raw) {
    holder.innerHTML = "";
    return;
  }

  const maxFloors = getMaxFloors();
  let floors = Math.floor(Number(raw));

  if (!Number.isFinite(floors) || floors < 1) {
    holder.innerHTML = "";
    return;
  }

  floors = Math.min(maxFloors, floors);

  if (Number(raw) > maxFloors && els.floors) {
    els.floors.value = String(maxFloors);
  }

  const oldValues = {};
  holder.querySelectorAll(".ufhFloorArea").forEach((input) => {
    oldValues[input.dataset.floorIndex] = input.value;
  });

  let html = "";

  for (let i = 0; i < floors; i += 2) {
    html += `<div class="row">`;

    for (let j = i; j < Math.min(i + 2, floors); j++) {
      const id = `ufhArea${j}`;
      const label = floorLabel(j);
      const val = oldValues[String(j)] || "";

      html += `
        <div>
          <label for="${id}">${label} size (m²)</label>
          <input
            id="${id}"
            class="ufhFloorArea"
            data-floor-index="${j}"
            type="number"
            min="0"
            step="0.1"
            value="${escapeHtml(val)}"
            placeholder="e.g. 80"
            inputmode="decimal"
          >
        </div>
      `;
    }

    html += `</div>`;
  }

  holder.innerHTML = html;
}

function setMsg(text, isError = false) {
  if (!els.msg) return;
  if (!text) {
    els.msg.classList.add("hide");
    els.msg.textContent = "";
    return;
  }
  els.msg.classList.remove("hide");
  els.msg.textContent = text;
  els.msg.style.borderColor = isError ? "#f2c6c6" : "#eee";
  els.msg.style.background = isError ? "#fff7f7" : "#fafafa";
}

function exportKitToCsv() {
  try {
    if (!lastKitLines || !lastKitLines.length) {
      setMsg("Nothing to export yet — calculate the kit first.", true);
      return;
    }

    const currency = lastKitLines.find(x => x.currency)?.currency || "GBP";

    const rows = [
      ["SKU", "QTY", `PRICE (${currency})`],
      ...lastKitLines.map(x => {
        const qty = Number(x.qty) || 0;
        const lineTotal = (Number(x.price) || 0) * qty;
        return [x.sku, String(qty), lineTotal.toFixed(2)];
      })
    ];

    const csv = rows
      .map(r => r.map(v => `"${String(v ?? "").replaceAll('"', '""')}"`).join(","))
      .join("\r\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `ufh-kit-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();

    URL.revokeObjectURL(url);
    setMsg("CSV exported.");
  } catch (err) {
    console.error(err);
    setMsg("Could not export CSV.", true);
  }
}

function resetAll() {
  if (els.floors) els.floors.value = "1";
onFloorsChange();
  if (els.spacing) els.spacing.value = "150";
  if (els.pipeType) els.pipeType.value = "PERT";
  if (els.fixing) els.fixing.value = "TACKER";
  if (els.controllerType) els.controllerType.value = "WIRED";
  if (els.controllerColour) els.controllerColour.value = "WHITE";
  if (els.phoneCountry) els.phoneCountry.value = "UK";
  if (els.projectStage) els.projectStage.value = "";

  if (els.bathrooms) els.bathrooms.value = "";
document.querySelectorAll(".ufhFloorArea").forEach(input => {
  input.value = "";
});
  if (els.zones) els.zones.value = "4";
  if (els.name) els.name.value = "";
  if (els.email) els.email.value = "";
  if (els.phone) els.phone.value = "";

  const internalName = document.getElementById("ufhInternalCustomerName");
const internalEmail = document.getElementById("ufhInternalCustomerEmail");
const internalQuote = document.getElementById("ufhInternalQuoteNumber");
const internalQuoteCountry = document.getElementById("ufhInternalQuoteCountry");
if (internalQuoteCountry) internalQuoteCountry.value = "GB";

if (internalName) internalName.value = "";
if (internalEmail) internalEmail.value = "";
if (internalQuote) internalQuote.value = "";

  document.querySelectorAll(".ufhExtra").forEach(cb => (cb.checked = false));

  onFloorsChange();

  if (els.totalArea) els.totalArea.textContent = "—";
  if (els.pipeRequired) els.pipeRequired.textContent = "—";
  if (els.circuits) els.circuits.textContent = "—";
  if (els.exVat) els.exVat.textContent = "—";
  if (els.incVat) els.incVat.textContent = "—";

  if (els.kitBody) {
    els.kitBody.innerHTML = `<tr><td colspan="5" class="empty">Enter your details and click “View Quote Now”.</td></tr>`;
  }

  if (els.addBtn) els.addBtn.disabled = true;
  if (els.exportBtn) els.exportBtn.disabled = true;
  if (els.printBtn) els.printBtn.disabled = true;

  lastKitLines = [];
  lastUFHCalc = null;
  lastInternalPdfUrl = "";
  leadCaptured = false;

  updatePrimaryButtonState();
  setMsg("");
}

function readUfhInputsOptions() {
  const maxFloors = getMaxFloors();
const floors = Math.max(1, Math.min(maxFloors, Math.floor(Number(els.floors?.value || 1))));
const bathrooms = Math.max(0, Math.floor(Number(els.bathrooms?.value || 0)));

const areas = Array.from({ length: floors }, (_, index) => {
  return Math.max(0, Number(document.getElementById(`ufhArea${index}`)?.value || 0));
});

  const spacing = Number(els.spacing?.value || 150);
  const pipeType = String(els.pipeType?.value || "PERT");
  const fixing = String(els.fixing?.value || "TACKER");

  const rawZones = Math.max(1, Math.floor(Number(els.zones?.value || 4)));
const zones = INTERNAL_MODE ? rawZones : Math.min(rawZones, 4);
  const controllerMake = String(document.getElementById("ufhControllerMake")?.value || "HEATMISER");
  const controllerType = String(els.controllerType?.value || "WIRED");
  const controllerColour = String(els.controllerColour?.value || "WHITE");

  const extrasSkus = [...document.querySelectorAll(".ufhExtra:checked")]
    .map(cb => String(cb.getAttribute("data-sku") || "").trim())
    .filter(Boolean);

  const bendSupports = extrasSkus.includes("ACC0094");

  return {
    inputs: { floors, bathrooms, areas },
    options: {
      spacing,
      pipeType,
      fixing,
      zones,
      controllerMake,
      controllerType,
      controllerColour,
      extrasSkus,
      bendSupports
    }
  };
}

async function calculateAndRender() {
  setMsg("");
  const runId = ++calcRunId;

  if (els.addBtn) els.addBtn.disabled = true;
  if (els.exportBtn) els.exportBtn.disabled = true;
  if (els.printBtn) els.printBtn.disabled = true;
  if (els.kitBody) els.kitBody.innerHTML = `<tr><td colspan="5" class="empty">Calculating…</td></tr>`;

  try {
    const { inputs, options } = readUfhInputsOptions();

    let payload;

    if (INTERNAL_MODE) {
  const internalMarket = getInternalMarketSettings();

  payload = {
    action: "build_kit",
    systemType: "ufh",
    inputs,
    options,
    customer: {
      name: String(
        document.getElementById("ufhInternalCustomerName")?.value ||
        "Customer"
      ).trim(),
      email: String(
        document.getElementById("ufhInternalCustomerEmail")?.value ||
        ""
      ).trim(),
    },
    internalQuoteNumber: String(
      document.getElementById("ufhInternalQuoteNumber")?.value ||
      ""
    ).trim(),
    meta: {
      source: "ufh-internal-calc",
      page: location.href,
      systemType: "ufh",
      internalMode: true,
      quoteType: "internal_pdf_quote",
      internalQuoteNumber: String(
        document.getElementById("ufhInternalQuoteNumber")?.value ||
        ""
      ).trim(),
      quoteCountry: internalMarket.quoteCountry,
      quoteCurrency: internalMarket.quoteCurrency,
      displayCurrency: internalMarket.quoteCurrency,
      presentmentCountry: internalMarket.quoteCountry,
      presentmentCurrency: internalMarket.quoteCurrency,
    },
  };
} else if (!leadCaptured) {
      const projectStage = String(els.projectStage?.value || "").trim();
      const name = String(els.name?.value || "").trim();
      const email = String(els.email?.value || "").trim();
      const phoneCountry = String(els.phoneCountry?.value || "UK").toUpperCase();
      const phone = normaliseUkIePhone(els.phone?.value || "", phoneCountry);

      if (!projectStage) {
        alert("Please select what stage you are at.");
        els.projectStage?.focus();
        return;
      }

      if (!name) {
        alert("Please enter your name.");
        els.name?.focus();
        return;
      }

      if (!isValidEmail(email)) {
        alert("Please enter a valid email address.");
        els.email?.focus();
        return;
      }

      payload = {
        action: "submit_lead_and_build",
        systemType: "ufh",
        name,
        email,
        projectStage,
        customer: {
          name,
          email,
          phone,
          phoneCountry,
          projectStage,
        },
        extra: {
          phone,
          phoneCountry,
          projectStage,
        },
        inputs,
        options,
        meta: {
          source: CRM_SOURCE_TAG,
          page: location.href,
          systemType: "ufh",
          phoneCountry,
          projectStage,
        },
      };
    } else {
      payload = {
        action: "build_kit",
        systemType: "ufh",
        inputs,
        options,
        meta: {
          source: "ufh-update-quote",
          page: location.href,
          systemType: "ufh",
        },
      };
    }

    const out = await heatProxy(payload);
    if (runId !== calcRunId) return;

    lastInternalPdfUrl = String(out?.pdfUrl || out?.meta?.pdfUrl || "");

    if (els.printBtn) {
      els.printBtn.disabled = !(INTERNAL_MODE && lastInternalPdfUrl);
    }

    if (!leadCaptured && payload.action === "submit_lead_and_build" && out?.ok) {
      leadCaptured = true;
      updatePrimaryButtonState();

      const shouldRedirectToThankYou = !INTERNAL_MODE;

      if (shouldRedirectToThankYou && out.redirectUrl) {
        setMsg("Quote ready. Redirecting...");
        window.location.href = out.redirectUrl;
        return;
      }

      if (shouldRedirectToThankYou && out.thankYouPageUrl) {
        setMsg("Quote ready. Redirecting...");
        window.location.href = out.thankYouPageUrl;
        return;
      }
    }

    const r = out?.results || {};

    if (els.totalArea) els.totalArea.textContent = `${Number(r.totalArea || 0).toFixed(1)} m²`;
    if (els.pipeRequired) els.pipeRequired.textContent = `${Number(r.totalPipeM || 0).toFixed(0)} m`;
    if (els.circuits) els.circuits.textContent = `${Number(r.totalCircuits || 0)}`;

    lastUFHCalc = {
      type: "ufh",
      quoteRef: String(out.quoteRef || out?.meta?.quoteRef || ""),
      inputs: out.inputs || inputs,
      options: out.options || options,
      results: out.results || {},
      totals: out?.kit?.totals || {},
    };

    lastKitLines = Array.isArray(out?.kit?.lines) ? out.kit.lines : [];

    const anyMissing = lastKitLines.some(x => !x.variantId);
    const anyOos = lastKitLines.some(x => x.availableForSale === false);

    if (!els.kitBody) return;

    if (!lastKitLines.length) {
      els.kitBody.innerHTML = `<tr><td colspan="5" class="empty">No kit lines returned.</td></tr>`;
    } else {
      els.kitBody.innerHTML = lastKitLines.map(line => {
        const imgUrl = String(line.image || "").trim();
        const img = imgUrl
          ? `<img class="thumb" src="${escapeHtml(imgUrl)}" alt="">`
          : `<div class="thumb" aria-hidden="true"></div>`;

        const qty = Number(line.qty || 0);
        const price = Number(line.price || 0);
        const currency = String(line.currency || "GBP");
        const lineTotal = price * qty;

        return `
          <tr>
            <td class="cImg">${img}</td>
            <td>
              <div style="font-weight:700">${escapeHtml(line.title || line.sku || "Item")}</div>
              ${line.note ? `<div class="hint" style="display:block !important">${escapeHtml(line.note)}</div>` : ``}
              ${(!line.variantId && !line.note) ? `<div class="hint" style="display:block !important;color:#b00020">SKU not found in Shopify</div>` : ``}
            </td>
            <td class="cSku">${escapeHtml(line.sku || "")}</td>
            <td class="cQty">${qty}</td>
            <td class="cPrice">${formatMoney(lineTotal, currency)}</td>
          </tr>
        `;
      }).join("");
    }

    let exText = out?.kit?.totals?.exVatText;
    let incText = out?.kit?.totals?.incVatText;

    if (!exText || !incText) {
      const fb = calcTotalsFromLines(lastKitLines, 0.2);
      exText = formatMoney(fb.exVat, fb.currency);
      incText = formatMoney(fb.incVat, fb.currency);
    }

    if (els.exVat) els.exVat.textContent = exText || "—";
    if (els.incVat) els.incVat.textContent = incText || "—";

    if (anyMissing || anyOos) {
      if (els.addBtn) els.addBtn.disabled = true;
      if (els.exportBtn) els.exportBtn.disabled = true;

      if (anyMissing) {
        setMsg("Some SKUs weren’t found in Shopify, so Add to cart is disabled. Check those SKUs exist and match variant SKUs.", true);
      } else {
        setMsg("One or more items are out of stock. Please request a quote.", true);
      }

      return;
    }

    if (els.addBtn) els.addBtn.disabled = false;
    if (els.exportBtn) els.exportBtn.disabled = false;

    if (INTERNAL_MODE && lastInternalPdfUrl) {
      setMsg("Kit calculated. You can now export CSV, add everything to cart, or open the PDF quote.");
    } else {
      setMsg(INTERNAL_MODE ? "Kit calculated. You can now export CSV or add everything to cart." : "Kit calculated. You can now add everything to cart.");
    }
  } catch (err) {
    console.error(err);

    if (els.kitBody) {
      els.kitBody.innerHTML = `<tr><td colspan="5" class="empty">Something went wrong.</td></tr>`;
    }

    if (els.exVat) els.exVat.textContent = "—";
    if (els.incVat) els.incVat.textContent = "—";

    setMsg(err?.message || "Something went wrong.", true);
  }
}

async function addKitToCart() {
  try {
    if (!lastKitLines?.length) return;

    const anyOos = lastKitLines.some(x => x.availableForSale === false);
    if (anyOos) {
      setMsg("One or more items are out of stock. Please request a quote.", true);
      return;
    }

    const items = lastKitLines
      .map(x => x?.variantId ? ({ id: Number(x.variantId), quantity: Number(x.qty || 0) }) : null)
      .filter(Boolean);

    if (items.length !== lastKitLines.length) {
      setMsg("Could not add kit to cart (one or more SKUs not found).", true);
      return;
    }

    const res = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items })
    });

    if (!res.ok) throw new Error("Could not add kit to cart.");

    const attrs = {
      attributes: {
        quote_source: "instant_quote_ufh",
        quote_system: "ufh",
        quote_ref: String(lastUFHCalc?.quoteRef || ""),
        quote_page: window.location.href,
        quote_customer_name: String(els.name?.value || window.UFH_CALC_CONFIG?.customerName || "").trim(),
        quote_customer_email: String(els.email?.value || window.UFH_CALC_CONFIG?.customerEmail || "").trim()
      }
    };

    const attrRes = await fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(attrs)
    });

    if (!attrRes.ok) {
      throw new Error("Kit added, but could not stamp cart attributes.");
    }

    setMsg("Added to cart. Redirecting to checkout…");
    window.location.href = "/checkout?instant=1";
  } catch (err) {
    console.error(err);
    setMsg(err?.message || "Could not add kit to cart.", true);
  }
}

function openInternalQuotePdf() {
  if (!INTERNAL_MODE) return;

  if (!lastInternalPdfUrl) {
    setMsg("Run the quote first, then open the PDF quote.", true);
    return;
  }

  window.open(lastInternalPdfUrl, "_blank", "noopener,noreferrer");
}